/**
 * Cross-workspace resource access → workspace-switch redirect.
 *
 * Shared by the REST (axios) and GraphQL (Apollo) error paths so a read of a resource that
 * lives in a *different but accessible* workspace always lands on the same `/error`
 * interstitial (which confirms, then runs `setActiveWorkspace`), regardless of which
 * transport surfaced the signal.
 *
 * Backend contract (accessible-but-inactive workspace only — a true outsider gets a 403 /
 * opaque error instead, with no switch signal):
 *  - REST: HTTP 409 with body `{ workspace_id, workspace_name }`.
 *  - GraphQL: an error with `extensions.workspaceSwitch = { workspace_id, workspace_name }`
 *    on the four single-object read queries (assessment / scenario / control / incident).
 */

export type WorkspaceSwitchTarget = { workspaceId: string; workspaceName: string };

/**
 * Pull the workspace-switch target out of a GraphQL error's `extensions`, or `undefined`
 * when the error is not a workspace-switch signal. Tolerant of missing / oddly-typed
 * fields: a signal without a usable string `workspace_id` is treated as not actionable.
 */
export const getWorkspaceSwitchTarget = (
  extensions: Record<string, unknown> | null | undefined,
): WorkspaceSwitchTarget | undefined => {
  const signal = extensions?.workspaceSwitch;
  if (!signal || typeof signal !== 'object') return undefined;

  const { workspace_id: id, workspace_name: name } = signal as {
    workspace_id?: unknown;
    workspace_name?: unknown;
  };
  if (typeof id !== 'string' || !id) return undefined;

  return { workspaceId: id, workspaceName: typeof name === 'string' ? name : '' };
};

/**
 * Hard-navigate to the `/error?status=409` interstitial, carrying the target workspace and
 * the current URL as `redirect_url` so the switch can return the user to where they were.
 * Every value is percent-encoded — workspace names are free text and may contain `&` / `=`.
 */
export const redirectToWorkspaceSwitch = (workspaceId?: string, workspaceName?: string): void => {
  const query = [
    'status=409',
    `workspace_id=${encodeURIComponent(workspaceId ?? '')}`,
    `workspace_name=${encodeURIComponent(workspaceName ?? '')}`,
    `redirect_url=${encodeURIComponent(window.location.href)}`,
  ].join('&');

  window.location.href = `/error?${query}`;
};

/**
 * Apollo-facing convenience: if any GraphQL error in the list carries the workspace-switch
 * signal, redirect and return the target that was acted on (so the caller can log/stop —
 * this is an expected redirect, not a toast/Sentry-worthy failure). Returns `undefined`
 * when no error carries the signal.
 */
export const maybeRedirectToWorkspaceSwitch = (
  errors: ReadonlyArray<{ extensions?: Record<string, unknown> | null }>,
): WorkspaceSwitchTarget | undefined => {
  const target = errors.map((error) => getWorkspaceSwitchTarget(error.extensions)).find(Boolean);
  if (target) redirectToWorkspaceSwitch(target.workspaceId, target.workspaceName);

  return target;
};
